import { Button, Tooltip } from '@chakra-ui/react';
import type { FC, ReactNode } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';

interface EditModeButtonsProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isNewPage?: boolean;
  isClone?: boolean;
  hasContent?: boolean;
  onSetViewMode: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export const EditModeButtons: FC<EditModeButtonsProps> = ({
  hasUnsavedChanges,
  isSaving,
  isNewPage = false,
  isClone = false,
  hasContent = false,
  onSetViewMode,
  onDiscard,
  onSave,
}) => {
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const buttonSize = isNewDashboardLayout ? 'xs' : undefined;

  let cancelLabel = 'Cancel';
  let cancelOnClick: () => void = onSetViewMode;

  if (isNewPage) {
    cancelLabel = 'Cancel';
    cancelOnClick = onDiscard;
  } else if (hasUnsavedChanges) {
    cancelLabel = 'Discard changes';
    cancelOnClick = onDiscard;
  } else {
    cancelLabel = 'Cancel';
    cancelOnClick = onSetViewMode;
  }

  let saveLabel = 'Save';
  let saveTooltipLabel: ReactNode;
  let saveTooltipOffset = 0;
  let saveIsDisabled = !hasUnsavedChanges;

  if (isClone || isNewPage) {
    saveLabel = 'Create';
    saveTooltipLabel = 'Save as a new page';
    saveTooltipOffset = -35;
    saveIsDisabled = !hasContent;
  } else if (hasUnsavedChanges) {
    saveTooltipLabel = 'Save changes';
    saveIsDisabled = false;
  } else {
    saveTooltipLabel = 'There are no changes to save';
    saveTooltipOffset = -60;
  }

  return (
    <>
      <Button
        variant="ghost"
        size={buttonSize}
        colorScheme="navyBlue"
        rounded="full"
        onClick={cancelOnClick}
        isDisabled={isSaving}
        aria-label={cancelLabel}
      >
        {cancelLabel}
      </Button>

      <Tooltip
        hasArrow
        variant="label"
        openDelay={300}
        offset={[saveTooltipOffset, 8]}
        label={saveTooltipLabel}
      >
        <Button
          isDisabled={saveIsDisabled}
          size={buttonSize}
          colorScheme="green"
          rounded="full"
          onClick={onSave}
          isLoading={isSaving}
          loadingText="Saving"
          aria-label={saveLabel}
        >
          {saveLabel}
        </Button>
      </Tooltip>
    </>
  );
};
